import { useCallback, useMemo, useState } from "react"

import type { AddedProduct, SalesProduct } from "../types"

import { useGetSalesProducts } from "./useGetSalesProducts"

import { MIN_SEARCH_LENGTH } from "@/constants/common"

interface UseProductSearchReturn {
  searchInputValue: string
  setSearchInputValue: (value: string) => void
  addedProducts: AddedProduct[]
  addedProductIds: Set<number>
  visibleResults: SalesProduct[]
  isFetching: boolean
  showSearchResults: boolean
  canFinalize: boolean
  handleSearch: (value: string) => void
  handleProductSuggestionSelect: (product: SalesProduct) => void
  handleSearchClear: () => void
  handleProductAdd: (product: SalesProduct) => void
  handleProductRemove: (productId: number) => void
  handleRequestQuoteChange: (productId: number, checked: boolean) => void
  handleExpandToggle: (productId: number) => void
}

export const useProductSearch = (initialAddedProducts: AddedProduct[] = []): UseProductSearchReturn => {
  const [addedProducts, setAddedProducts] = useState<AddedProduct[]>(initialAddedProducts)
  const [searchInputValue, setSearchInputValue] = useState("")
  const [submittedQuery, setSubmittedQuery] = useState("")
  const [submittedProductId, setSubmittedProductId] = useState<number | undefined>(undefined)

  const addedProductIds = useMemo(
    () => new Set(addedProducts.map((addedProduct) => addedProduct.product.productId)),
    [addedProducts]
  )

  const { products, isFetching } = useGetSalesProducts({
    searchQuery: submittedQuery,
    productId: submittedProductId,
  })

  const visibleResults = useMemo(
    () => products.filter((product) => !addedProductIds.has(product.productId)),
    [products, addedProductIds]
  )

  const showSearchResults =
    submittedProductId !== undefined ||
    (submittedQuery.length >= MIN_SEARCH_LENGTH && searchInputValue.trim().length >= MIN_SEARCH_LENGTH)
  const canFinalize = addedProducts.some((addedProduct) => addedProduct.requestQuote)

  const handleSearchInputChange = useCallback((value: string) => {
    setSearchInputValue(value)
    if (value.trim().length < MIN_SEARCH_LENGTH) {
      setSubmittedQuery("")
      setSubmittedProductId(undefined)
    }
  }, [])

  const handleSearch = (value: string) => {
    if (value.trim().length < MIN_SEARCH_LENGTH) return
    setSubmittedQuery(value.trim())
    setSubmittedProductId(undefined)
  }

  const handleProductSuggestionSelect = (product: SalesProduct) => {
    setSearchInputValue(product.companyProductDescription ?? "")
    setSubmittedProductId(product.productId)
    setSubmittedQuery("")
  }

  const handleSearchClear = () => {
    setSearchInputValue("")
    setSubmittedQuery("")
    setSubmittedProductId(undefined)
  }

  const handleProductAdd = (product: SalesProduct) => {
    setAddedProducts((prev) => [...prev, { product, requestQuote: false, isExpanded: false }])
    handleSearchClear()
  }

  const handleProductRemove = (productId: number) => {
    setAddedProducts((prev) => prev.filter((addedProduct) => addedProduct.product.productId !== productId))
  }

  const handleRequestQuoteChange = (productId: number, checked: boolean) => {
    setAddedProducts((prev) =>
      prev.map((addedProduct) =>
        addedProduct.product.productId === productId ? { ...addedProduct, requestQuote: checked } : addedProduct
      )
    )
  }

  const handleExpandToggle = (productId: number) => {
    setAddedProducts((prev) =>
      prev.map((addedProduct) =>
        addedProduct.product.productId === productId
          ? { ...addedProduct, isExpanded: !addedProduct.isExpanded }
          : addedProduct
      )
    )
  }

  return {
    searchInputValue,
    setSearchInputValue: handleSearchInputChange,
    addedProducts,
    addedProductIds,
    visibleResults,
    isFetching,
    showSearchResults,
    canFinalize,
    handleSearch,
    handleProductSuggestionSelect,
    handleSearchClear,
    handleProductAdd,
    handleProductRemove,
    handleRequestQuoteChange,
    handleExpandToggle,
  }
}
